<template>
  <Layout>
    <div class="faq-page">
      <!-- Hero Section -->
      <section class="faq-hero bg-gradient-primary text-white py-5">
        <div class="container">
          <div class="row align-items-center">
            <div class="col-lg-12 mx-auto text-center">
              <h1 class="display-4 font-weight-bold mb-3">FAQs</h1>
              <p class="lead mb-2">OVVURA – Unlock Radiance!</p>
              <p class="mb-4">Effective Date: August 7, 2025 | Version: 2.0</p>
              <div class="d-flex justify-content-center flex-wrap">
                <b-button variant="light" class="mr-3 mb-2" @click="scrollToFAQs">
                  <b-icon icon="arrow-down" class="mr-2"></b-icon>
                  Browse FAQs
                </b-button>
                <b-button variant="outline-light" class="mb-2" @click="contactSupport">
                  <b-icon icon="headset" class="mr-2"></b-icon>
                  Contact Support
                </b-button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- Main Content -->
      <div class="container my-5" id="faq-content">
        <div class="row">
          <div class="col-lg-12 mx-auto">
            <!-- Introduction -->
            <b-card no-body class="border-0 shadow-sm mb-4">
              <b-card-body>
                <div class="d-flex align-items-center mb-4">
                  <div class="icon-circle bg-primary text-white mr-3">
                    <i class="mdi mdi-help-circle-outline" style="font-size: 1.5rem;"></i>
                  </div>
                  <h3 class="mb-0">Frequently Asked Questions</h3>
                </div>
                <p>Find answers to the most common questions about OVVURA's services, booking process, payments, and more. Can't find what you're looking for? Our support team is here to help!</p>
              </b-card-body>
            </b-card>

            <!-- FAQ Categories -->
            <div class="faq-categories mb-4">
              <h4 class="mb-3">Browse by Category</h4>
              <div class="category-buttons">
                <b-button
                  v-for="category in categories"
                  :key="category.id"
                  :variant="selectedCategory === category.id ? 'primary' : 'outline-primary'"
                  class="mr-2 mb-2"
                  @click="filterByCategory(category.id)"
                >
                  <i :class="category.icon" class="mr-2"></i>
                  {{ category.name }}
                </b-button>
              </div>
            </div>

            <!-- FAQ Accordion -->
            <b-card no-body class="border-0 shadow-sm">
              <b-card-header class="bg-light">
                <h4 class="mb-0 d-flex align-items-center justify-content-between">
                  <span>Questions & Answers</span>
                  <b-badge variant="primary" pill>{{ filteredFAQs.length }} FAQs</b-badge>
                </h4>
              </b-card-header>
              <b-card-body class="p-4">
                <div v-if="filteredFAQs.length === 0" class="text-center py-5">
                  <i class="mdi mdi-magnify" style="font-size: 3rem; color: #ccc;"></i>
                  <p class="mt-3 text-muted">No FAQs found matching your search.</p>
                  <b-button variant="outline-primary" @click="clearFilters">
                    Clear Filters
                  </b-button>
                </div>
                
                <b-accordion v-else>
                  <b-accordion-item
                    v-for="(faq) in filteredFAQs"
                    :key="faq.id"
                    :title="faq.question"
                    :header-class="'faq-header'"
                    :body-class="'faq-body'"
                  >
                    <template #title>
                      <div class="d-flex align-items-center">
                        <div class="faq-number mr-3">
                          {{ faq.id }}
                        </div>
                        <span>{{ faq.question }}</span>
                        <b-badge
                          v-if="faq.category"
                          :variant="getCategoryVariant(faq.category)"
                          class="ml-auto mr-3"
                        >
                          {{ getCategoryName(faq.category) }}
                        </b-badge>
                      </div>
                    </template>
                    
                    <div class="faq-answer" v-html="faq.answer"></div>
                    
                    <div v-if="faq.relatedLinks && faq.relatedLinks.length" class="related-links mt-3">
                      <h6>Related:</h6>
                      <div class="d-flex flex-wrap">
                        <b-button
                          v-for="link in faq.relatedLinks"
                          :key="link"
                          variant="outline-secondary"
                          size="sm"
                          class="mr-2 mb-2"
                          @click="scrollToFAQ(link)"
                        >
                          Question {{ link }}
                        </b-button>
                      </div>
                    </div>
                    
                    <div class="faq-footer mt-3 pt-3 border-top">
                      <div class="d-flex justify-content-between align-items-center">
                        <div class="helpful-section">
                          <span class="text-muted mr-2">Was this helpful?</span>
                          <b-button variant="outline-success" size="sm" class="mr-2" @click="markHelpful(faq.id, true)">
                            <b-icon icon="hand-thumbs-up"></b-icon> Yes
                          </b-button>
                          <b-button variant="outline-danger" size="sm" @click="markHelpful(faq.id, false)">
                            <b-icon icon="hand-thumbs-down"></b-icon> No
                          </b-button>
                        </div>
                        <small class="text-muted">Updated: {{ faq.updated }}</small>
                      </div>
                    </div>
                  </b-accordion-item>
                </b-accordion>
              </b-card-body>
            </b-card>

            <!-- Contact Support Card -->
            <b-card no-body class="border-0 shadow-sm mt-4 bg-gradient-light">
              <b-card-body class="text-center">
                <div class="support-icon mb-3">
                  <i class="mdi mdi-headset" style="font-size: 3rem; color: #6e8efb;"></i>
                </div>
                <h4 class="mb-3">Still need help?</h4>
                <p class="text-muted mb-4">Can't find the answer you're looking for? Our friendly support team is ready to assist you.</p>
                
                <div class="contact-methods">
                  <div class="row">
                    <div class="col-md-4 mb-3">
                      <div class="contact-method">
                        <i class="mdi mdi-email text-primary mb-2" style="font-size: 2rem;"></i>
                        <h6>Email Support</h6>
                        <a href="mailto:support@ovvura.ae" class="btn btn-outline-primary btn-sm">
                          support@ovvura.ae
                        </a>
                      </div>
                    </div>
                    <div class="col-md-4 mb-3">
                      <div class="contact-method">
                        <i class="mdi mdi-phone text-success mb-2" style="font-size: 2rem;"></i>
                        <h6>Phone Support</h6>
                        <a href="tel:+971525495829" class="btn btn-outline-success btn-sm">
                          +971 52 549 5829
                        </a>
                      </div>
                    </div>
                    <div class="col-md-4 mb-3">
                      <div class="contact-method">
                        <i class="mdi mdi-chat text-info mb-2" style="font-size: 2rem;"></i>
                        <h6>Live Chat</h6>
                        <b-button variant="outline-info" size="sm">
                          Mon–Sat, 9 AM–6 PM
                        </b-button>
                      </div>
                    </div>
                  </div>
                </div>
              </b-card-body>
            </b-card>
          </div>
        </div>
      </div>
    </div>
  </Layout>
</template>

<script>
import Layout from "../../layouts/main";

export default {
  components: { Layout },
  data() {
    return {
      searchQuery: '',
      selectedCategory: 'all',
      categories: [
        { id: 'all', name: 'All FAQs', icon: 'mdi mdi-view-grid' },
        { id: 'general', name: 'General', icon: 'mdi mdi-information' },
        { id: 'booking', name: 'Booking', icon: 'mdi mdi-calendar-check' },
        { id: 'payment', name: 'Payment', icon: 'mdi mdi-credit-card' },
        { id: 'services', name: 'Services', icon: 'mdi mdi-spa' },
        { id: 'account', name: 'Account', icon: 'mdi mdi-account' },
        { id: 'support', name: 'Support', icon: 'mdi mdi-help-circle' }
      ],
      faqs: [
        {
          id: 1,
          question: "What is OVVURA?",
          answer: "OVVURA is an online platform offering a comprehensive range of <strong>beauty, healthcare, and wellness services</strong>. We deliver a seamless booking experience—from dental check-ups and spa treatments to haircuts and yoga sessions—and feature a curated e-commerce shop stocked with unique self-care products.",
          category: 'general',
          updated: 'Aug 7, 2025',
          relatedLinks: [2, 4]
        },
        {
          id: 2,
          question: "How do I book an appointment on OVVURA?",
          answer: `Booking an appointment is easy:<br>
            <ol>
              <li>Visit our website or download the OVVURA mobile app</li>
              <li>Browse services across beauty, healthcare, and wellness categories</li>
              <li>Select the service and provider that best suit your needs</li>
              <li>Choose an available time slot and confirm your booking</li>
            </ol>
            <p>You will receive a <strong>confirmation email or SMS</strong> once your appointment is successfully booked.</p>`,
          category: 'booking',
          updated: 'Aug 7, 2025',
          relatedLinks: [6, 7]
        },
        {
          id: 3,
          question: "Is OVVURA available throughout the UAE?",
          answer: "Yes! Whether you're in <strong>Dubai, Abu Dhabi, Sharjah</strong>, or any other emirate, you can discover and book services from trusted providers in your area.",
          category: 'general',
          updated: 'Aug 7, 2025'
        },
        {
          id: 4,
          question: "What types of services can I book through OVVURA?",
          answer: `We offer a wide range of services:<br><br>
            <strong>🌟 Beauty Services:</strong> Haircuts, hair coloring, makeup artistry, facials, manicures, pedicures<br><br>
            <strong>🏥 Healthcare Services:</strong> Dental check-ups, consultations, physiotherapy, dermatology, and more<br><br>
            <strong>🧘 Wellness Services:</strong> Spa treatments, massages, yoga, meditation, fitness sessions<br><br>
            <strong>📦 Personalized Packages:</strong> Combinations of services for a complete self-care experience`,
          category: 'services',
          updated: 'Aug 7, 2025',
          relatedLinks: [1, 9]
        },
        {
          id: 5,
          question: "How do I pay for my booking?",
          answer: `We accept multiple payment methods:<br><br>
            <strong>💳 Credit/Debit Cards:</strong> Visa, Mastercard, UnionPay, American Express<br>
            <strong>🌐 Payment Gateways:</strong> Secure online options via trusted platforms<br>
            <strong>💰 Cash on Delivery:</strong> Available for select services (check availability at checkout)<br><br>
            Payment is processed at the time of booking. For product purchases, payment is required at checkout.`,
          category: 'payment',
          updated: 'Aug 7, 2025',
          relatedLinks: [13]
        },
        {
          id: 6,
          question: "Can I reschedule or cancel my appointment?",
          answer: `Yes! You may reschedule or cancel up to <strong>24 hours before</strong> your appointment without penalty. Cancellations within 24 hours may incur a fee, depending on the provider's policy.<br><br>
            <strong>To reschedule or cancel:</strong><br>
            <ol>
              <li>Log in to your OVVURA account</li>
              <li>Go to "My Appointments"</li>
              <li>Select the appointment and follow the prompts to reschedule or cancel</li>
            </ol>`,
          category: 'booking',
          updated: 'Aug 7, 2025',
          relatedLinks: [7, 8]
        },
        {
          id: 7,
          question: "What if I need to modify or update my booking?",
          answer: "For changes such as service type or time slot, follow the same steps as rescheduling. If you encounter any issues, our <strong>support team</strong> is happy to assist.",
          category: 'booking',
          updated: 'Aug 7, 2025',
          relatedLinks: [6, 11]
        },
        {
          id: 8,
          question: "Do I need to create an account to use OVVURA?",
          answer: `An account is <strong>optional but recommended</strong> for an enhanced experience. With an OVVURA account, you can:<br><br>
            <ul>
              <li>💖 Save favorite providers and services</li>
              <li>📅 Manage all appointments in one place</li>
              <li>🎉 Access exclusive offers and discounts</li>
              <li>📦 Track order history and purchased products</li>
            </ul>`,
          category: 'account',
          updated: 'Aug 7, 2025',
          relatedLinks: [12]
        },
        {
          id: 9,
          question: "Can I buy products through OVVURA?",
          answer: "Yes! Browse our curated <strong>beauty, healthcare, and wellness products</strong>, add your selections to the cart, and check out via our secure payment system.",
          category: 'services',
          updated: 'Aug 7, 2025',
          relatedLinks: [5, 13]
        },
        {
          id: 10,
          question: "Are the service providers on OVVURA licensed and certified?",
          answer: "Absolutely! Every provider on OVVURA holds the <strong>necessary licenses, certifications, and hands-on experience</strong> to deliver top-quality services. We vet each partner to ensure safety, professionalism, and customer satisfaction.",
          category: 'services',
          updated: 'Aug 7, 2025'
        },
        {
          id: 11,
          question: "How do I contact OVVURA customer support?",
          answer: `Our support team is here to help:<br><br>
            <strong>📧 Email:</strong> support@ovvura.ae<br>
            <strong>📞 Phone:</strong> +971 52 549 5829<br>
            <strong>💬 Live Chat:</strong> Available on our website (Mon–Sat, 9 AM–6 PM)`,
          category: 'support',
          updated: 'Aug 7, 2025'
        },
        {
          id: 12,
          question: "Are there any promotions or discounts available?",
          answer: "Yes! We regularly run <strong>promotions, discounts, and special deals</strong>. Sign up for our newsletter or follow us on social media to stay current on the latest offers. You may also find package discounts when booking multiple services.",
          category: 'general',
          updated: 'Aug 7, 2025',
          relatedLinks: [8]
        },
        {
          id: 13,
          question: "Is OVVURA safe to use for online payments?",
          answer: "Absolutely! We employ <strong>secure encryption and trusted payment gateways</strong> to protect your payment details. All transactions comply with industry standards to keep your data safe and confidential.",
          category: 'payment',
          updated: 'Aug 7, 2025',
          relatedLinks: [5]
        },
        {
          id: 14,
          question: "How do I leave feedback about a service or provider?",
          answer: "After your appointment, you can <strong>rate and review</strong> your provider directly on the platform. You're also welcome to send us feedback via our support channels if you have concerns or suggestions.",
          category: 'support',
          updated: 'Aug 7, 2025',
          relatedLinks: [11]
        },
        {
          id: 15,
          question: "What makes OVVURA different from other platforms?",
          answer: `OVVURA stands out because of:<br><br>
            <ul>
              <li>🌟 A holistic mix of beauty, healthcare, and wellness services in one place</li>
              <li>📱 An intuitive, seamless booking experience</li>
              <li>🛍️ Exclusive, handpicked products designed to elevate your self-care</li>
              <li>🏆 A steadfast commitment to quality, reliability, and customer satisfaction</li>
            </ul>
            <p><strong>Ready to unlock your radiance?</strong> Explore services, book appointments, and shop products—all in one place.</p>`,
          category: 'general',
          updated: 'Aug 7, 2025',
          relatedLinks: [1, 4]
        }
      ],
      filteredFAQs: []
    }
  },
  mounted() {
    this.filteredFAQs = this.faqs;
  },
  methods: {
    scrollToFAQs() {
      const element = document.getElementById('faq-content');
      element.scrollIntoView({ behavior: 'smooth' });
    },
    contactSupport() {
      window.location.href = 'mailto:support@ovvura.ae';
    },
    filterFAQs() {
      this.applyFilters();
    },
    filterByCategory(categoryId) {
      this.selectedCategory = categoryId;
      this.applyFilters();
    },
    applyFilters() {
      let filtered = this.faqs;
      
      // Filter by category
      if (this.selectedCategory !== 'all') {
        filtered = filtered.filter(faq => faq.category === this.selectedCategory);
      }
      
      // Filter by search query
      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase();
        filtered = filtered.filter(faq => 
          faq.question.toLowerCase().includes(query) || 
          faq.answer.toLowerCase().includes(query)
        );
      }
      
      this.filteredFAQs = filtered;
    },
    clearFilters() {
      this.searchQuery = '';
      this.selectedCategory = 'all';
      this.filteredFAQs = this.faqs;
    },
    getCategoryName(categoryId) {
      const category = this.categories.find(cat => cat.id === categoryId);
      return category ? category.name : '';
    },
    getCategoryVariant(categoryId) {
      const variants = {
        general: 'primary',
        booking: 'success',
        payment: 'warning',
        services: 'info',
        account: 'secondary',
        support: 'danger'
      };
      return variants[categoryId] || 'primary';
    },
    scrollToFAQ(faqId) {
      // Find the accordion item and expand it
      const faqIndex = this.filteredFAQs.findIndex(faq => faq.id === faqId);
      if (faqIndex !== -1) {
        this.$nextTick(() => {
          const element = document.querySelector(`[aria-controls="accordion-item-${faqIndex}"]`);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            // Simulate click to expand if not already expanded
            setTimeout(() => {
              if (!element.getAttribute('aria-expanded') || element.getAttribute('aria-expanded') === 'false') {
                element.click();
              }
            }, 500);
          }
        });
      }
    },
    markHelpful(faqId, helpful) {
      // In a real implementation, this would send data to a backend
      this.$bvToast.toast(
        helpful ? 'Thank you for your feedback!' : 'We\'ll work on improving this answer.',
        {
          title: 'Feedback Received',
          variant: helpful ? 'success' : 'warning',
          solid: true,
          autoHideDelay: 3000
        }
      );
    }
  }
}
</script>

<style scoped>
.faq-page {
  overflow-x: hidden;
}

.faq-hero {
  background: linear-gradient(135deg, #6e8efb, #a777e3);
  padding: 5rem 0;
}

.search-section {
  border-bottom: 1px solid #dee2e6;
}

.search-box {
  box-shadow: 0 4px 6px rgba(0,0,0,0.1);
  border-radius: 10px;
  overflow: hidden;
}

.icon-circle {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.faq-categories {
  background: white;
  padding: 20px;
  border-radius: 10px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.05);
}

.category-buttons {
  display: flex;
  flex-wrap: wrap;
}

.faq-header {
  background-color: #f8f9fa !important;
  border-bottom: 1px solid #dee2e6;
}

.faq-number {
  background: linear-gradient(135deg, #6e8efb, #a777e3);
  color: white;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 0.9rem;
}

.faq-body {
  padding: 25px !important;
}

.faq-answer {
  line-height: 1.7;
  color: #444;
}

.faq-answer strong {
  color: #6e8efb;
}

.faq-answer ol, .faq-answer ul {
  margin: 15px 0;
  padding-left: 20px;
}

.faq-answer li {
  margin-bottom: 8px;
}

.related-links {
  background-color: #f8f9fa;
  padding: 15px;
  border-radius: 8px;
  border-left: 4px solid #6e8efb;
}

.faq-footer {
  background-color: #fafafa;
  margin: 0 -25px -25px -25px;
  padding: 15px 25px 20px 25px !important;
}

.helpful-section {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
}

.bg-gradient-light {
  background: linear-gradient(135deg, #f8f9fa, #e9ecef);
}

.support-icon {
  margin-bottom: 20px;
}

.contact-method {
  padding: 20px;
  border-radius: 10px;
  transition: transform 0.2s ease;
}

.contact-method:hover {
  transform: translateY(-2px);
}

@media (max-width: 768px) {
  .faq-hero {
    padding: 3rem 0;
  }
  
  .d-flex.justify-content-center {
    flex-direction: column;
    align-items: center;
  }
  
  .d-flex.justify-content-center .btn {
    margin: 0 0 10px 0 !important;
    width: 200px;
  }
  
  .category-buttons {
    justify-content: center;
  }
  
  .category-buttons .btn {
    margin: 5px;
  }
  
  .faq-number {
    width: 25px;
    height: 25px;
    font-size: 0.8rem;
  }
  
  .d-flex.justify-content-between {
    flex-direction: column;
    gap: 15px;
  }
  
  .helpful-section {
    justify-content: center;
  }
}

@media (max-width: 576px) {
  .faq-body {
    padding: 20px 15px !important;
  }
  
  .faq-footer {
    margin: 0 -15px -20px -15px;
    padding: 15px !important;
  }
}
</style>